import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { apiErrorText, useCreateCeoDebtInstrumentMutation } from '@shared/api';
import { todayIso } from '@shared/lib/localDate';
import { parseDecimal, parseMoney } from '@shared/lib/money';
import { Button } from '@shared/ui/Button';
import {
  EMPTY_DEBT_FORM,
  checkDebtForm,
  plural,
  ruDate,
  type DebtForm,
  type SchedulePreview,
} from './addDebtModel';
import {
  ButtonGroup,
  CheckboxLabel,
  FieldError,
  FieldHint,
  FormBox,
  FormErrorMessage,
  FormField,
  FormGrid,
  FormLabel,
  FormTitle,
  Input,
  SchedulePreviewText,
  Select,
} from './styles';

const PARSERS = { money: parseMoney, decimal: parseDecimal };

interface AddDebtModalProps {
  /** Договор создан — показать его карточку. */
  onCreated: (instrumentId: string) => void;
  onClose: () => void;
}

function previewText(p: SchedulePreview): string {
  if (p.empty) return p.empty;
  const range = `первый — ${ruDate(p.first)}, последний — ${ruDate(p.last)}`;
  return p.kind === 'quarterly'
    ? `График: ${p.count} ${plural(p.count, 'комиссия', 'комиссии', 'комиссий')} раз в квартал, ${range}.`
    : `График: ${p.count} ${plural(p.count, 'платёж', 'платежа', 'платежей')}, ${range}.`;
}

/**
 * Новый договор кредита, лизинга или банковской гарантии. Ставка, даты и день платежа не
 * подставляются: ноль — допустимая ставка, пустое поле — ошибка у поля. До записи видно,
 * сколько платежей будет в автографике, первую и последнюю дату. Форма очищается, только
 * когда сервер договор создал.
 */
export function AddDebtModal({ onCreated, onClose }: AddDebtModalProps) {
  const [form, setForm] = useState<DebtForm>(EMPTY_DEBT_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [createDebt, { isLoading: isCreating }] = useCreateCeoDebtInstrumentMutation();
  const uid = useId();
  const fid = (name: keyof DebtForm) => `${uid}-${name}`;

  const check = checkDebtForm(form, PARSERS, todayIso());
  const guarantee = form.instType === 'bank_guarantee';
  const leasing = form.instType === 'leasing';

  function set<K extends keyof DebtForm>(key: K, value: DebtForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  /** Ошибка поля — после первой попытки сохранить. */
  function fieldError(name: keyof DebtForm): string | undefined {
    return submitted ? check.errors[name] : undefined;
  }

  /** Поле с подписью; ошибка или подсказка — под полем и связаны с ним. */
  function field(
    name: keyof DebtForm,
    label: string,
    control: (a11y: {
      id: string;
      'aria-invalid'?: true;
      'aria-describedby'?: string;
    }) => ReactNode,
    hint?: string,
  ) {
    const error = fieldError(name);
    const noteId = `${fid(name)}-note`;
    return (
      <FormField>
        <FormLabel htmlFor={fid(name)}>{label}</FormLabel>
        {control({
          id: fid(name),
          'aria-invalid': error ? true : undefined,
          'aria-describedby': error || hint ? noteId : undefined,
        })}
        {error ? (
          <FieldError id={noteId}>{error}</FieldError>
        ) : (
          hint && <FieldHint id={noteId}>{hint}</FieldHint>
        )}
      </FormField>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!check.payload || isCreating) return;
    setServerError(null);
    try {
      const created = await createDebt(check.payload).unwrap();
      onCreated(created.instrument.id);
    } catch (err) {
      setServerError(apiErrorText(err, 'Не удалось создать договор.'));
    }
  }

  const scheduleError = check.errors.schedule;

  return (
    <FormBox as="form" onSubmit={handleSubmit} noValidate>
      <FormTitle>
        Новый кредитный или лизинговый договор (с автогенерацией помесячного графика платежей)
      </FormTitle>
      <FormGrid>
        {field('instType', 'Тип договора', (a11y) => (
          <Select
            {...a11y}
            value={form.instType}
            onChange={(e) => {
              const next = e.target.value;
              set('instType', next);
              // Комиссия по гарантии — фиксированная ставка; саму ставку не подставляем.
              if (next === 'bank_guarantee') set('rateType', 'fixed');
            }}
          >
            <option value="credit_line_vkl">ВКЛ (возобновляемая кредитная линия)</option>
            <option value="project_finance">НКЛ / Проектное финансирование</option>
            <option value="term_loan">Срочный кредит</option>
            <option value="leasing">Лизинг спецтехники / оборудования</option>
            <option value="bank_guarantee">Банковская гарантия</option>
          </Select>
        ))}
        {field('contractNo', 'Номер договора', (a11y) => (
          <Input
            {...a11y}
            placeholder="Например: ВКЛ-2026/12"
            value={form.contractNo}
            onChange={(e) => set('contractNo', e.target.value)}
          />
        ))}
        {field('creditor', guarantee ? 'Банк-гарант' : 'Кредитор / лизингодатель', (a11y) => (
          <Input {...a11y} value={form.creditor} onChange={(e) => set('creditor', e.target.value)} />
        ))}
        {field('title', 'Наименование / цель финансирования', (a11y) => (
          <Input {...a11y} value={form.title} onChange={(e) => set('title', e.target.value)} />
        ))}
        {field(
          'totalLimit',
          guarantee ? 'Лимит гарантии, ₽' : 'Лимит договора, ₽',
          (a11y) => (
            <Input
              {...a11y}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="Например: 12 000 000"
              value={form.totalLimit}
              onChange={(e) => set('totalLimit', e.target.value)}
            />
          ),
          guarantee ? 'Забалансовое обязательство' : undefined,
        )}
        {field(
          'principalBalance',
          'Текущий остаток долга, ₽',
          (a11y) => (
            <Input
              {...a11y}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.principalBalance}
              onChange={(e) => set('principalBalance', e.target.value)}
            />
          ),
          guarantee ? 'Для гарантии обычно 0; пусто — 0' : 'Пусто — равен лимиту',
        )}
        {field('rateType', 'Тип ставки', (a11y) => (
          <Select {...a11y} value={form.rateType} onChange={(e) => set('rateType', e.target.value)}>
            <option value="floating_cbr">Плавающая (КС ЦБ + маржа)</option>
            <option value="fixed">Фиксированная ставка</option>
            <option value="subsidized">Льготная субсидируемая</option>
          </Select>
        ))}
        {field(
          'baseRate',
          guarantee ? 'Ставка комиссии, % годовых' : 'Базовая ставка, % годовых',
          (a11y) => (
            <Input
              {...a11y}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="Например: 18,25"
              value={form.baseRate}
              onChange={(e) => set('baseRate', e.target.value)}
            />
          ),
          'Беспроцентный договор — 0',
        )}
        {form.rateType === 'floating_cbr' &&
          field(
            'marginRate',
            'Маржа банка к ключевой ставке, %',
            (a11y) => (
              <Input
                {...a11y}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="Например: 2,5"
                value={form.marginRate}
                onChange={(e) => set('marginRate', e.target.value)}
              />
            ),
            'Без маржи — 0',
          )}
        {field('startDate', 'Дата начала (выдачи)', (a11y) => (
          <Input
            {...a11y}
            type="date"
            value={form.startDate}
            onChange={(e) => set('startDate', e.target.value)}
          />
        ))}
        {field('endDate', 'Дата окончания', (a11y) => (
          <Input
            {...a11y}
            type="date"
            value={form.endDate}
            onChange={(e) => set('endDate', e.target.value)}
          />
        ))}
        {field(
          'paymentDay',
          form.autoGenerateSchedule ? 'День платежа (1–31)' : 'День платежа (1–31), необязательно',
          (a11y) => (
            <Input
              {...a11y}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="Например: 20"
              value={form.paymentDay}
              onChange={(e) => set('paymentDay', e.target.value)}
            />
          ),
          '29–31 в коротком месяце — последний день месяца',
        )}
        {leasing && (
          <>
            {field('leasedAssetName', 'Предмет лизинга (техника)', (a11y) => (
              <Input
                {...a11y}
                value={form.leasedAssetName}
                onChange={(e) => set('leasedAssetName', e.target.value)}
              />
            ))}
            {field('leasedAssetCount', 'Количество единиц техники', (a11y) => (
              <Input
                {...a11y}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={form.leasedAssetCount}
                onChange={(e) => set('leasedAssetCount', e.target.value)}
              />
            ))}
          </>
        )}
        {field('collateral', 'Залог / обеспечение', (a11y) => (
          <Input {...a11y} value={form.collateral} onChange={(e) => set('collateral', e.target.value)} />
        ))}
        {field('covenants', 'Ковенанты банка', (a11y) => (
          <Input {...a11y} value={form.covenants} onChange={(e) => set('covenants', e.target.value)} />
        ))}
      </FormGrid>
      <CheckboxLabel>
        <input
          type="checkbox"
          checked={form.autoGenerateSchedule}
          onChange={(e) => set('autoGenerateSchedule', e.target.checked)}
        />
        <span>
          {guarantee
            ? 'Сформировать график ежеквартальных комиссий по БГ (без погашения основного долга)'
            : 'Автоматически построить помесячный график погашения основного долга и процентов'}
        </span>
      </CheckboxLabel>
      {form.autoGenerateSchedule && (
        <SchedulePreviewText $error={Boolean(scheduleError)} aria-live="polite">
          {scheduleError
            ? scheduleError
            : check.preview
              ? previewText(check.preview)
              : 'Укажите даты договора и день платежа — здесь появится итог графика.'}
        </SchedulePreviewText>
      )}
      {serverError && <FormErrorMessage role="alert">{serverError}</FormErrorMessage>}
      <ButtonGroup>
        <Button $size="sm" type="submit" disabled={isCreating}>
          {isCreating
            ? 'Создание…'
            : form.autoGenerateSchedule
              ? 'Создать договор и построить график'
              : 'Создать договор без графика'}
        </Button>
        <Button $size="sm" $variant="ghost" type="button" onClick={onClose} disabled={isCreating}>
          Отмена
        </Button>
      </ButtonGroup>
    </FormBox>
  );
}
